// Libs
import classNames from 'classnames/bind';
import { Box, Button, Grid2, Stack, Typography } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
// Components, Layouts, Pages
import { BaseDatePicker, BaseSelect, BaseTimePicker } from '~/components/common';
// Others
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_1000,
  EMPTY_STRING,
} from '~/utils/constants/common';
import { IBaseOption, IQueryParamBase } from '~/utils/interface/common';
import useDebounce from '~/utils/hooks/useDebounce';
import { useAppDispatch } from '~/utils/redux/hooks';
import { getFullName } from '~/utils/helper/common';
import { IEmployee } from '~/utils/interface/employee';
import { getEmployeeList } from '~/thunks/employee/employeeThunk';
import { IFormEstimate } from '~/utils/interface/estimate';
import { TypeTabCreateEstimateEnum } from '~/utils/enum/common';
// Styles, images, icons
import styles from './CreateEstimateScheduleTab.module.scss';

type Props = {
  onEdit?: (data: IFormEstimate, type: string) => void;
  onCancelEdit: () => void;
};

const cx = classNames.bind(styles);

const CreateEstimateScheduleTab = (props: Props) => {
  //#region Destructuring Props
  const { onEdit, onCancelEdit } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/estimate']);
  const dispatch = useAppDispatch();
  const { id: estimateId } = useParams();
  const {
    control,
    getValues,
    formState: { errors, isDirty },
  } = useFormContext<IFormEstimate>();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoadingEmployee, setIsLoadingEmployee] = useState<boolean>(false);
  const [employeeOptions, setEmployeeOptions] = useState<IBaseOption[]>([]);
  const [employeeSearchKey, setEmployeeSearchKey] = useState<string>(EMPTY_STRING);
  const debounceEmployeeSearchKey = useDebounce(employeeSearchKey, DEFAULT_DEBOUNCE_DELAY_TIME);
  const currentPageEmployee = BASE_PAGINATION_DEFAULT_CURRENT_PAGE;
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    const params: IQueryParamBase = {
      page: currentPageEmployee,
      limit: DEFAULT_LIMIT_1000,
      ...(debounceEmployeeSearchKey && { textSearch: debounceEmployeeSearchKey }),
    };

    handleGetEmployeeList(params);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPageEmployee, debounceEmployeeSearchKey]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleSearchEmployeeChange = (searchKey: string) => {
    setEmployeeSearchKey(searchKey);
  };

  const handleConvertEmployeeOptions = (employeeList: IEmployee[]) => {
    const options: IBaseOption[] = employeeList.map((employee) => ({
      value: employee.id,
      label: getFullName({ firstName: employee.firstName, lastName: employee.lastName }),
    }));

    if (currentPageEmployee === BASE_PAGINATION_DEFAULT_CURRENT_PAGE) {
      setEmployeeOptions(options);
    } else {
      const newOptions = employeeOptions.concat(options);
      setEmployeeOptions(newOptions);
    }
  };

  const handleGetEmployeeList = (params: IQueryParamBase) => {
    setIsLoadingEmployee(true);

    dispatch(getEmployeeList(params))
      .unwrap()
      .then((resp) => {
        const { responses, pagination } = resp.data;
        if (!responses || employeeOptions?.length === pagination?.totalItems) {
          return;
        }

        handleConvertEmployeeOptions(responses);
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoadingEmployee(false);
      });
  };

  const handleSubmitFormSchedule = () => {
    const dataForm = getValues();
    const dataBody: IFormEstimate = {
      clientId: EMPTY_STRING,
      startDate: dataForm?.startDate || EMPTY_STRING,
      startTime: dataForm?.startTime || EMPTY_STRING,
      endDate: dataForm?.endDate || EMPTY_STRING,
      endTime: dataForm?.endTime || EMPTY_STRING,
      assigneeIds: dataForm?.assigneeIds || [],
    };

    onEdit && onEdit(dataBody, TypeTabCreateEstimateEnum.SCHEDULE);
  };
  //#endregion Handle Function

  return (
    <Box component='div' className={cx('container')} id='createEstimateScheduleTabComponent'>
      <Grid2 container rowSpacing={'24px'} columnSpacing={'12px'}>
        <Grid2 size={12} container rowSpacing={'24px'} columnSpacing={'12px'} alignItems='flex-end'>
          <Grid2 size={6}>
            <Controller
              name='startDate'
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseDatePicker
                  label={t('field_schedule_from_label')}
                  value={value}
                  onChange={onChange}
                  errorMessage={errors?.startDate?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              name='startTime'
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseTimePicker value={value} onChange={onChange} errorMessage={errors?.startTime?.message} />
              )}
            />
          </Grid2>
        </Grid2>

        <Grid2 container size={12} rowSpacing={'24px'} columnSpacing={'12px'} alignItems='flex-end'>
          <Grid2 size={6}>
            <Controller
              name='endDate'
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseDatePicker
                  value={value}
                  label={t('field_schedule_to_label')}
                  onChange={onChange}
                  errorMessage={errors?.endDate?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              name='endTime'
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseTimePicker value={value} onChange={onChange} errorMessage={errors?.endTime?.message} />
              )}
            />
          </Grid2>

          <Grid2 size={12}>
            <Typography component={'p'} className={cx('timezone')}>
              {t('field_schedule_time_zone', { data: Intl.DateTimeFormat().resolvedOptions().timeZone })}
            </Typography>
          </Grid2>

          <Grid2 size={12}>
            <Controller
              name='assigneeIds'
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseSelect
                  multiple={true}
                  label={t('field_schedule_assignees_label')}
                  value={value || EMPTY_STRING}
                  onChange={onChange}
                  isLoading={isLoadingEmployee}
                  options={employeeOptions}
                  onSearchChange={handleSearchEmployeeChange}
                  errorMessage={errors.assigneeIds?.message}
                />
              )}
            />
          </Grid2>
        </Grid2>
        {estimateId && (
          <Stack
            direction={'row'}
            alignItems={'center'}
            justifyContent={'flex-end'}
            component={'div'}
            spacing={'12px'}
            className={cx('formFooter')}
          >
            <Button variant='outlined' onClick={onCancelEdit}>
              {t('common_action_cancel', { ns: 'common/base' })}
            </Button>

            <Button variant='contained' type='button' onClick={handleSubmitFormSchedule} disabled={!isDirty}>
              {t('common_action_save', { ns: 'common/base' })}
            </Button>
          </Stack>
        )}
      </Grid2>
    </Box>
  );
};

export default CreateEstimateScheduleTab;
